import { api } from '../api.js';
import { subscribe, getState } from '../state.js';
import { getTranslatedFields, translate } from '../lib/i18n.js';
import { createTimelineRenderer } from '../components/timelineRenderer.js';
import { showConceptInSidebar, showCrosslinkInSidebar } from '../components/graphSidebar.js';
import type { ApiGraphNode, ApiRelationTypeTranslation } from '../../shared/types.js';

interface RelationType {
  id: number | null;
  label: string;
  translations?: ApiRelationTypeTranslation[];
}

interface TimelineEdge {
  source: string;
  target: string;
  relation_type_id?: number | null;
}

interface TimelineData {
  nodes: ApiGraphNode[];
  edges: TimelineEdge[];
  relationTypes?: RelationType[];
}

function getTranslatedRelationType(rt: RelationType): string {
  const { lang } = getState();
  if (!lang || !rt.translations || rt.translations.length === 0) return rt.label;
  const tr = rt.translations.find((r: ApiRelationTypeTranslation) => r.lang.toLowerCase() === lang.toLowerCase());
  return (tr && tr.name) ? tr.name : rt.label;
}

export async function renderTimelineView(container: HTMLElement): Promise<void | (() => void)> {
  container.innerHTML = `<div class="view"><p class="view__subtitle" style="padding:4rem 2rem;text-align:center;">${translate('common.loading', 'loading...')}</p></div>`;

  let renderer: ReturnType<typeof createTimelineRenderer> | null = null;
  let langUnsub: (() => void) | null = null;
  let keyHandler: ((e: KeyboardEvent) => void) | null = null;
  let activeTypes: Set<number | null> | null = null; // null = all visible

  try {
    const timelineData = await api.getTimelineGraph() as TimelineData;
    const relationTypes: RelationType[] = timelineData.relationTypes || [];

    // Track which types are visible (all on by default)
    activeTypes = new Set(relationTypes.map((rt: RelationType) => rt.id));

    function render(): void {
      if (renderer) { renderer.destroy(); renderer = null; }
      if (keyHandler) { document.removeEventListener('keydown', keyHandler); keyHandler = null; }
      container.innerHTML = '';

      const view = document.createElement('div');
      view.className = 'view view--timeline';

      // Header with relation type toggles
      const header = document.createElement('div');
      header.className = 'timeline-header';

      if (timelineData.nodes.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'not-found';
        empty.style.minHeight = '40vh';
        empty.innerHTML = `<p class="not-found__message">${translate('timeline.empty', 'No concepts with dates yet.')}</p>`;
        view.appendChild(empty);
        container.appendChild(view);
        return;
      }

      // Relation type filter chips
      if (relationTypes.length > 0) {
        const filterBar = document.createElement('div');
        filterBar.className = 'timeline-filter';

        const filterLabel = document.createElement('span');
        filterLabel.className = 'timeline-filter__label';
        filterLabel.textContent = translate('timeline.relations', 'Relations');
        filterBar.appendChild(filterLabel);

        for (const rt of relationTypes) {
          const chip = document.createElement('button');
          chip.className = 'timeline-chip' + (activeTypes!.has(rt.id) ? ' timeline-chip--active' : '');
          chip.textContent = getTranslatedRelationType(rt);
          chip.dataset.typeId = rt.id === null ? 'null' : String(rt.id);

          chip.addEventListener('click', () => {
            const typeId = rt.id;
            if (activeTypes!.has(typeId)) {
              activeTypes!.delete(typeId);
              chip.classList.remove('timeline-chip--active');
            } else {
              activeTypes!.add(typeId);
              chip.classList.add('timeline-chip--active');
            }
            if (renderer) {
              renderer.setVisibleTypes([...activeTypes!].filter((id): id is number => id !== null));
            }
          });

          filterBar.appendChild(chip);
        }

        header.appendChild(filterBar);
      }

      view.appendChild(header);

      // Graph wrapper
      const graphWrapper = document.createElement('div');
      graphWrapper.className = 'timeline-graph-wrapper';

      const timelineContainer = document.createElement('div');
      timelineContainer.className = 'timeline-graph-container';
      graphWrapper.appendChild(timelineContainer);

      // Sidebar (hidden by default)
      const sidebar = document.createElement('div');
      sidebar.className = 'graph-sidebar';
      sidebar.style.display = 'none';
      sidebar.innerHTML = `
        <button class="graph-sidebar__close">&times;</button>
        <div class="graph-sidebar__content"></div>
      `;
      graphWrapper.appendChild(sidebar);

      // Legend
      const legend = document.createElement('div');
      legend.className = 'timeline-legend';
      legend.innerHTML = `
        <span class="tree-legend__item"><span class="tree-legend__key">${translate('legend.click', 'click')}</span> ${translate('timeline.legend_concept', 'node to view concept')}</span>
        <span class="tree-legend__item"><span class="tree-legend__key">${translate('legend.scroll', 'scroll')}</span> ${translate('timeline.legend_scroll_pan', 'to scroll')}</span>
        <span class="tree-legend__item"><span class="tree-legend__key">${translate('timeline.legend_ctrl_scroll', 'ctrl + scroll')}</span> ${translate('legend.to_zoom', 'to zoom')}</span>
      `;
      timelineContainer.appendChild(legend);

      view.appendChild(graphWrapper);
      container.appendChild(view);

      // Sidebar logic
      const closeSidebar = (): void => {
        sidebar.style.display = 'none';
      };
      (sidebar.querySelector('.graph-sidebar__close') as HTMLButtonElement).addEventListener('click', closeSidebar);

      keyHandler = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') closeSidebar();
      };
      document.addEventListener('keydown', keyHandler);

      const showNodeSidebar = (slug: string): void => {
        const node = timelineData.nodes.find((n: ApiGraphNode) => n.slug === slug);
        if (!node) return;
        showConceptInSidebar(sidebar, node);
      };

      const showEdgeSidebar = ({ edgeData }: { edgeData: TimelineEdge; sourceSlug: string; targetSlug: string }): void => {
        if (!edgeData) return;
        showCrosslinkInSidebar(sidebar, edgeData as unknown as Parameters<typeof showCrosslinkInSidebar>[1], timelineData.nodes);
      };

      // Translate node titles for renderer
      const translatedData = {
        nodes: timelineData.nodes.map((n: ApiGraphNode) => ({
          ...n,
          title: getTranslatedFields(n).title,
        })),
        edges: timelineData.edges,
      };

      renderer = createTimelineRenderer(timelineContainer, translatedData, {
        onNodeClick: showNodeSidebar,
        onEdgeClick: showEdgeSidebar,
      });

      // Apply current filter
      if (activeTypes && renderer) {
        renderer.setVisibleTypes([...activeTypes].filter((id): id is number => id !== null));
      }
    }

    render();

    langUnsub = subscribe('lang', render);

    return () => {
      if (renderer) renderer.destroy();
      if (langUnsub) langUnsub();
      if (keyHandler) document.removeEventListener('keydown', keyHandler);
    };
  } catch (err) {
    container.innerHTML = `<div class="view"><p class="form-error" style="padding:4rem 2rem;text-align:center;">${translate('timeline.error', 'Failed to load timeline')}: ${(err as Error).message}</p></div>`;
  }
}
